import './KeyValueTable.css';
import React, { useMemo } from 'react';
import classnames from 'classnames';
import GlobalProps from '../globalProps';
import { compact, isEmpty } from 'lodash';
import { SpacingSizes } from '../Stack/Stack';
import { t } from '../../locales/translate';

export interface KeyValue {
  key: string;
  keyNode?: React.ReactNode;
  value?: React.ReactNode;
  style?: React.CSSProperties;
  className?: string;
  labelClass?: string;
  type?: 'row' | 'label';
  preserveLineBreaks?: boolean;
}

interface Props extends GlobalProps {
  items: Array<KeyValue | null | false>;
  labelWidth?: number;
  valueWidth?: number | string;
  emptyMsg?: string;
  emptyValueMsg?: string;
  displayEmpty?: boolean;
  fullWidth?: boolean;
  variant?: 'default' | 'table';
  gap?: SpacingSizes;
}

export function KeyValueTable({
  items: rawItems,
  labelWidth,
  valueWidth,
  emptyMsg,
  emptyValueMsg = '-',
  displayEmpty = true,
  className,
  style,
  fullWidth = true,
  variant,
  gap,
}: Props): JSX.Element | null {
  const items = compact(rawItems);
  function emptyValues(): boolean {
    const empty = items.filter((item) => item.value !== undefined || item.key === 'separator');
    if (empty.length === 0) {
      return true;
    }
    return false;
  }
  if (emptyValues()) {
    if (!displayEmpty) {
      return null;
    }
    return <div>{emptyMsg || 'All values are empty'}</div>;
  }
  return (
    <table
      className={classnames(
        'key-value-table',
        {
          'key-value-table--full-width': fullWidth,
          [`key-value-table--${variant}`]: variant,
          [`key-value-table--gap-${gap}`]: gap,
        },
        className
      )}
      style={style}
    >
      <tbody>
        {items.map((item, index) => {
          if (item.key === 'separator') {
            return <tr className="key-value-table__separator" style={item.style} key={`${item.key}-${index}`}></tr>;
          }
          if (item.key === 'title') {
            return (
              <tr className="key-value-table__title" style={item.style} key={`${item.key}-${index}`}>
                <td>{item.value ?? emptyValueMsg}</td>
              </tr>
            );
          }

          if (!displayEmpty && !item.value) {
            return null;
          }
          return (
            <tr className={item.className} style={item.style} key={item.key}>
              <td className={classnames('key-value-table__label', item.labelClass)} style={{ width: labelWidth }}>
                {item.keyNode || item.key}
              </td>
              <td
                className={classnames(
                  'key-value-table__name',
                  item.preserveLineBreaks && 'key-value-table--preserve-line-breaks'
                )}
                style={{ width: valueWidth }}
              >
                {item.value ?? emptyValueMsg}
              </td>
              {variant === 'table' && <td className="key-value-table__filler"></td>}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

export function useKeyValueData(data: Record<string, unknown> | undefined | null): KeyValue[] {
  return useMemo((): KeyValue[] => {
    if (!data || isEmpty(data)) {
      return [{ key: t.general.noDataToShow, value: '' }];
    }

    return Object.entries(data).map(([key, value]) => ({
      key,
      value,
    }));
  }, [data]);
}
